import React, { FC, useEffect, useId, useState } from 'react';
import type { OrganisationSeatPlan } from '@dreamai/types';
import {
  MAX_PLAN_MULTIPLIER,
  SUGGESTED_PLAN_MULTIPLIERS,
  describePlan,
  isSuggestedMultiplier,
} from './organisationPlans';

interface OrganisationPlanPickerProps {
  /** Current multiple of the seat price; null means "inherit" (groups only) */
  value: number | null;
  seatPlan: OrganisationSeatPlan;
  disabled?: boolean;
  /** When set, offers a first option that follows the organisation plan */
  inherit?: { label: string; multiplier: number };
  onChange: (multiplier: number | null) => void;
}

/**
 * Three ready-made monthly plans plus a custom amount. Admins see only what a
 * member costs per month and how many credits that buys; the underlying
 * multiple of the seat price never appears.
 */
const OrganisationPlanPicker: FC<OrganisationPlanPickerProps> = ({
  value,
  seatPlan,
  disabled = false,
  inherit,
  onChange,
}) => {
  const name = useId();
  const [customOpen, setCustomOpen] = useState(
    value !== null && !isSuggestedMultiplier(value),
  );
  const [customValue, setCustomValue] = useState(
    value !== null && !isSuggestedMultiplier(value)
      ? value
      : SUGGESTED_PLAN_MULTIPLIERS[0] + 1,
  );

  const customSelected = value !== null && !isSuggestedMultiplier(value);
  useEffect(() => {
    if (
      value !== customValue &&
      (value === null || isSuggestedMultiplier(value))
    ) {
      setCustomOpen(false);
    }
  }, [value, customValue]);
  const showCustom = customOpen || customSelected;
  const custom = describePlan(customValue, seatPlan);

  const pick = (multiplier: number | null) => {
    setCustomOpen(false);
    onChange(multiplier);
  };

  const pickCustom = () => {
    setCustomOpen(true);
    onChange(customValue);
  };

  const stepCustom = (delta: number) => {
    const next = Math.min(
      MAX_PLAN_MULTIPLIER,
      Math.max(1, customValue + delta),
    );
    setCustomValue(next);
    onChange(next);
  };

  return (
    <div
      className='organisation-plan-picker'
      role='radiogroup'
      data-testid='organisation-plan-picker'
    >
      {inherit && (
        <label
          className={`organisation-plan-option${value === null ? ' selected' : ''}`}
        >
          <input
            type='radio'
            name={name}
            checked={value === null}
            disabled={disabled}
            onChange={() => pick(null)}
          />
          <span className='organisation-plan-option-body'>
            <span className='organisation-plan-price'>{inherit.label}</span>
            <span className='organisation-plan-credits'>
              {describePlan(inherit.multiplier, seatPlan).price}/month per
              member · {describePlan(inherit.multiplier, seatPlan).credits}{' '}
              credits a month
            </span>
          </span>
        </label>
      )}

      {SUGGESTED_PLAN_MULTIPLIERS.map((multiplier) => {
        const { price, credits, bonusPercent } = describePlan(
          multiplier,
          seatPlan,
        );
        const selected = value === multiplier && !customOpen;
        return (
          <label
            key={multiplier}
            className={`organisation-plan-option${selected ? ' selected' : ''}`}
          >
            <input
              type='radio'
              name={name}
              checked={selected}
              disabled={disabled}
              onChange={() => pick(multiplier)}
            />
            <span className='organisation-plan-option-body'>
              <span className='organisation-plan-price'>
                {price}
                <span className='organisation-plan-per'>
                  {' '}
                  /month per member
                </span>
              </span>
              <span className='organisation-plan-credits'>
                {credits} credits a month
                {bonusPercent > 0 && (
                  <span className='organisation-plan-bonus'>
                    {' '}
                    · {bonusPercent}% extra
                  </span>
                )}
              </span>
            </span>
          </label>
        );
      })}

      <label
        className={`organisation-plan-option${showCustom ? ' selected' : ''}`}
      >
        <input
          type='radio'
          name={name}
          checked={showCustom}
          disabled={disabled}
          onChange={pickCustom}
        />
        <span className='organisation-plan-option-body'>
          <span className='organisation-plan-price'>
            Custom amount
            {showCustom && (
              <span className='organisation-plan-per'>
                {' '}
                · {custom.price} /month per member
              </span>
            )}
          </span>
          <span className='organisation-plan-credits'>
            {showCustom
              ? `${custom.credits} credits a month`
              : 'Choose how much to spend per member — bigger plans get up to 50% extra credits'}
            {showCustom && custom.bonusPercent > 0 && (
              <span className='organisation-plan-bonus'>
                {' '}
                · {custom.bonusPercent}% extra
              </span>
            )}
          </span>
          {showCustom && (
            <span className='organisation-plan-custom'>
              <button
                type='button'
                className='organisation-plan-step'
                aria-label='Less'
                disabled={disabled || customValue <= 1}
                onClick={() => stepCustom(-1)}
              >
                −
              </button>
              <button
                type='button'
                className='organisation-plan-step'
                aria-label='More'
                disabled={disabled || customValue >= MAX_PLAN_MULTIPLIER}
                onClick={() => stepCustom(1)}
              >
                +
              </button>
            </span>
          )}
        </span>
      </label>
    </div>
  );
};

export default OrganisationPlanPicker;
